// ============================================
// BrowseBooks.jsx - COMPLETE BROWSE SYSTEM
// Save as: src/BrowseBooks.jsx
// ============================================

import React, { useState } from 'react';
import { 
  Book, 
  Filter, 
  BookOpen,
  Coins,
  Clock,
  ExternalLink
} from 'lucide-react';
import {
  CountrySelector,
  CountryBadges,
  adaptAmazonLink
} from './CountrySystem';

const BrowseBooks = ({
  currentUser, books, claimedBooks, reviews,
  handleClaimBook, setCurrentUser, setUsers,
  loading  // ← add this
}) => {
  const [selectedGenre, setSelectedGenre] = useState('All Genres');

  // ============================================
  // HELPER FUNCTIONS


  const ownerCanShowBooks = (owner) => {
  if (!owner) return false;

  // Admin always visible
  if (owner.isAdmin) return true;

  // Active review subscription
  if (
    owner.reviewSubscription?.active &&
    new Date(owner.reviewSubscription.expiresAt) > new Date()
  ) {
    return true;
  }

  // 5-day free trial logic
  if (owner.trialStartDate) {
    const daysUsed = Math.floor(
      (Date.now() - new Date(owner.trialStartDate)) / (1000 * 60 * 60 * 24)
    );

    // Trial valid AND under 5 reviews
    if (daysUsed <= 5 && (owner.reviewsReceived || 0) < 5) {
      return true;
    }
  }

  // Otherwise → hide books
  return false;
};

  // ============================================

 const getVisibleBooksForBrowse = () => {
  if (!books || !Array.isArray(books)) return [];
  if (!currentUser?.id) return [];

  // ✅ Build the set of master book ids the current user has already reviewed
  // (Piece 5a). Rejected/pending/approved reviews all count — if they
  // submitted on that master at all, they're done with it. Per-user only.
  const reviewedBookIds = (reviews || [])
    .filter(r => r.reviewerId === currentUser.id)
    .map(r => r.bookId);

  const reviewedMasterIds = new Set(
    reviewedBookIds.map(bookId => {
      const reviewedBook = books.find(b => b.id === bookId);
      // Copies should always have masterBookId; fall back to the book's own
      // id as the master key if it's missing.
      return reviewedBook?.masterBookId || bookId;
    })
  );

  return books
    .filter(book => {
        // ✅ Admin-approved only
        if (book.status !== 'approved') return false;

        // ✅ Approved books stay hidden from Browse until the author requests a review (Piece 2)
        if (book.reviewRequested !== true) return false;

        // ✅ Hide books whose author's trial ended (no active subscription)
        if (book.authorGated === true) return false;

        // ✅ Hide copies currently under review (Piece 4b)
        if (book.underReview === true) return false;

        // ✅ Hide copies of a master the current user has already reviewed (Piece 5a)
        if (book.masterBookId && reviewedMasterIds.has(book.masterBookId)) return false;

      // ❌ Don't show own books
      if (book.uploadedBy === currentUser.id) return false;

      return true;
    })
    .sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0));
};



  const GENRES = [
    'All Genres', 'Fiction', 'Non-Fiction', 'Romance',
    'Mystery, Thriller & Suspense', 'Science Fiction', 'Fantasy',
    'Horror', 'Historical', 'Young Adult', 'Children\'s Books',
    'Biography & Memoir', 'Self-Help', 'Business & Money'
  ];

  // ============================================
  // RENDER
  // ============================================

  const visibleBooks = getVisibleBooksForBrowse();
  const filteredBooks = selectedGenre === 'All Genres' 
    ? visibleBooks 
    : visibleBooks.filter(book => book.genre === selectedGenre);

  // Calculate genre counts
  const genreCounts = {};
  GENRES.forEach(genre => {
    genreCounts[genre] = genre === 'All Genres' 
      ? visibleBooks.length 
      : visibleBooks.filter(book => book.genre === genre).length;
  });

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* ============================================ */}
      {/* LEFT SIDEBAR - GENRE FILTER (DESKTOP ONLY) */}
      {/* ============================================ */}
      <div className="hidden lg:block w-64 flex-shrink-0">
        <div className="bg-white rounded-xl shadow-md p-4 sticky top-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5" />
            All Genres
          </h3>
          
          <div className="space-y-1">
            {GENRES.map((genre) => {
              const count = genreCounts[genre];
              const isSelected = selectedGenre === genre;
              
              return (
                <button
                  key={genre}
                  onClick={() => setSelectedGenre(genre)}
                  disabled={count === 0}
                  className={`
                    w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 text-sm
                    ${isSelected
                      ? 'bg-indigo-600 text-white font-semibold shadow-md'
                      : count === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-indigo-600'
                    }
                  `}
                >
                  {genre}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* MAIN CONTENT AREA */}
      {/* ============================================ */}
      <div className="flex-1">
        <h2 className="text-2xl font-bold mb-4">Browse Books to Review</h2>
        
        {/* ============================================ */}
        {/* MOBILE: DROPDOWN GENRE SELECTOR */}
        {/* ============================================ */}
        <div className="lg:hidden mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Genre
          </label>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-medium focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.5em 1.5em',
              }}
            >
              {GENRES.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* ============================================ */}
        {/* COUNTRY SELECTOR */}
        {/* ============================================ */}
        <CountrySelector 
          currentUser={currentUser}
          onCountryChange={(newCountry) => {
            setCurrentUser(prev => ({ ...prev, country: newCountry }));
            setUsers(prev => prev.map(u => 
              u.id === currentUser.id ? { ...u, country: newCountry } : u
            ));
          }}
        />

        {/* ============================================ */}
        {/* RESULTS SUMMARY */}
        {/* ============================================ */}
        {visibleBooks.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <Book className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No books available to review right now.</p>
            <p className="text-sm text-gray-500 mt-2">
              Check back later or try selecting a different country.
            </p>
          </div>
        ) : (
          <div>
            {/* Results count and clear filter */}
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {selectedGenre === 'All Genres' 
                  ? `Showing all ${filteredBooks.length} books`
                  : `${filteredBooks.length} ${selectedGenre} ${filteredBooks.length === 1 ? 'book' : 'books'}`
                }
              </p>
              {selectedGenre !== 'All Genres' && (
                <button
                  onClick={() => setSelectedGenre('All Genres')}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Clear Filter
                </button>
              )}
            </div>

            {/* ============================================ */}
            {/* BOOK GRID */}
            {/* ============================================ */}
            {filteredBooks.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-8 text-center">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium mb-2">No books in {selectedGenre}</p>
                <button
                  onClick={() => setSelectedGenre('All Genres')}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                >
                  Show All Books
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredBooks.map(book => {
                  const alreadyClaimed = claimedBooks.find(
                    cb => cb.bookId === book.id && cb.userId === currentUser.id
                  );
                  const hasReviewed = reviews.find(
                    r => r.bookId === book.id && r.reviewerId === currentUser.id
                  );
                  const canClaim = !alreadyClaimed && !hasReviewed;
                  
                  const turnaroundDays = book.turnaroundDays || 7;
                  const deadlineDate = new Date(Date.now() + turnaroundDays * 24 * 60 * 60 * 1000);
                  const formattedDeadline = deadlineDate.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  });
                  
                  return (
                    <div 
                      key={book.id} 
                      className="bg-white rounded-xl shadow-md p-3 relative hover:shadow-lg transition flex flex-col"
                    >
                      {/* Country badges */}
                      <CountryBadges targetCountries={book.targetCountries} />

                      {/* Cover image */}
                      {book.coverImage && (
                        <div className="w-full overflow-hidden rounded-lg mb-2">
                          <img 
                            src={book.coverImage} 
                            alt={book.title} 
                            className="w-full h-32 sm:h-40 lg:h-48 object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }} 
                          />
                        </div>
                      )}
                      
                      {/* Title and author */}
                      <h3 className="font-bold text-sm line-clamp-2 mb-1">{book.title}</h3>
                      <p className="text-xs text-gray-600 mb-2">by {book.author}</p>
                      
                      {/* Genre badge */}
                      <span className="inline-block px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded mb-2 w-fit">
                        {book.genre}
                      </span>
                      
                      {/* Description */}
                      <p className="text-xs text-gray-700 line-clamp-2 mb-2 flex-grow">
                        {book.description}
                      </p>

                      {/* Coins and deadline */}
                      <div className="flex flex-col gap-1 mb-2">
                        <div className="flex items-center gap-1">
                          <Coins className="w-3 h-3 text-amber-600" />
                          <span className="font-bold text-amber-700 text-xs">
                            Earn {book.coinsRequired}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-orange-600" />
                          <span className="text-xs text-orange-700 font-medium">
                            Due {formattedDeadline}
                          </span>
                        </div>
                      </div>
                      
                      {/* Amazon link */}
                      <a 
                        href={adaptAmazonLink(book.amazonLink, currentUser?.country)} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="text-xs text-blue-600 mb-2 inline-flex items-center hover:text-blue-700"
                      >
                        <ExternalLink className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">Review on Amazon</span>
                      </a>

                      {/* Claim button */}
                      {canClaim ? (
                        <button 
                          onClick={() => handleClaimBook(book.id)} 
                          className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition font-semibold text-xs"
                        >
                          Claim
                        </button>
                      ) : alreadyClaimed ? (
                        <button 
                          disabled 
                          className="w-full bg-gray-200 text-gray-500 py-2 rounded-lg cursor-not-allowed text-xs font-medium"
                        >
                          Claimed
                        </button>
                      ) : (
                        <button 
                          disabled 
                          className="w-full bg-gray-200 text-gray-500 py-2 rounded-lg cursor-not-allowed text-xs font-medium"
                        >
                          Reviewed
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseBooks;